import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/useLenis";
import { HERO } from "@/constants/testIds";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Grain from "@/components/Grain";
import Reveal from "@/components/Reveal";

const ATHENA_IMG = "/media/athena.webp";
const CLOUDS_MP4 = "/media/cara-loop.mp4";
const CLOUDS_WEBM = "/media/cara-loop.webm";
const CLOUDS_POSTER = "/media/cara-loop-poster.jpg";

/* ------------------------------------ hero -------------------------------- */
function Hero() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start end", "end start"],
  });
  const ghostY = useTransform(scrollYProgress, [0, 1], ["0%", "-24%"]);
  const ghostO = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.7, 0.35]);

  const headline = [
    { text: "The Capital", italic: false, grad: false },
    { text: "Behind", italic: false, grad: false },
    { text: "Care Convergence.", italic: true, grad: true },
  ];

  return (
    <section
      id="top"
      ref={wrapRef}
      className="relative min-h-[88vh] md:min-h-[94vh] flex flex-col justify-end overflow-hidden"
    >
      <div className="hero-athena" aria-hidden="true">
        <div className="hero-athena__img" style={{ backgroundImage: `url(${ATHENA_IMG})` }} />
        <div className="hero-athena__ember" />
        <div className="hero-athena__fade" />
      </div>

      <motion.div style={{ y: ghostY, opacity: ghostO }} className="ghost-word" aria-hidden="true">
        CARA
      </motion.div>

      <div className="container-editorial relative z-10 hero-mobile-pt grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-8 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="overline mb-4 md:mb-5"
          >
            A Private Venture Fund of Funds &mdash; In Partnership with Marram Ventures
          </motion.div>

          <h1 className="hero-h1">
            {headline.map((line, idx) => (
              <span className="mask-line" key={idx}>
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 1.2,
                    ease: [0.76, 0, 0.24, 1],
                    delay: 0.25 + idx * 0.14,
                  }}
                  className={line.italic ? "italic-serif" : ""}
                  style={line.grad ? { display: "inline-block" } : { display: "block" }}
                >
                  {line.grad ? <span className="grad-text">{line.text}</span> : line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
            className="mt-6 md:mt-8 max-w-2xl text-[16px] md:text-[17px]"
            style={{ color: "var(--stone)", lineHeight: 1.6 }}
          >
            Cara Capital is built for the leaders shaping dentistry&rsquo;s future. Direct access
            to the ventures, founders, and breakthrough companies defining whole person health.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 1.05 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href="/event"
              onClick={(e) => {
                e.preventDefault();
                navigate("/event");
              }}
              className="btn btn-ember"
              data-testid={HERO.requestInvite}
            >
              RSVP for the event →
            </a>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("about");
              }}
              className="btn btn-outline"
              data-testid={HERO.exploreFund}
            >
              Explore the Fund
            </a>
          </motion.div>

          <motion.a
            href="/event"
            onClick={(e) => {
              e.preventDefault();
              navigate("/event");
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.2 }}
            className="mt-10 md:mt-14 inline-flex items-center gap-4 overline"
            style={{ cursor: "pointer" }}
          >
            <span className="inline-block w-8 h-px" style={{ background: "var(--ink-20)" }} />
            Wed, Feb 24, 2027 &mdash; Chicago &mdash; See the event →
          </motion.a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ stats strip ------------------------------- */
function Stats() {
  const items = [
    { k: "$136B", v: "Global oral health market by 2027" },
    { k: "<3%", v: "Of venture capital reaches oral health" },
    { k: "1 in 3", v: "Adults living with untreated oral disease" },
  ];
  return (
    <section className="hairline-t hairline-b">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-3">
        {items.map((it, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className={`py-10 md:py-14 pr-6 ${i > 0 ? "md:pl-10 md:hairline-l" : ""}`}>
              <div
                className="font-serif"
                style={{
                  fontSize: "clamp(40px, 5vw, 68px)",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                }}
              >
                {it.k}
              </div>
              <div className="mt-4 text-[14px]" style={{ color: "var(--stone)" }}>
                {it.v}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* --------------------------------- about ---------------------------------- */
function About() {
  return (
    <section id="about" className="section">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">About Cara Capital</div>
          </Reveal>
        </div>
        <div className="md:col-span-9 max-w-4xl">
          <Reveal delay={0.05}>
            <h2
              className="font-serif"
              style={{ fontSize: "clamp(38px, 5.4vw, 76px)", lineHeight: 1.02, letterSpacing: "-0.02em" }}
            >
              Built for the leaders shaping{" "}
              <span className="italic-serif grad-text">dentistry&rsquo;s future.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p
              className="mt-8 text-[16.5px] md:text-[17.5px] max-w-3xl"
              style={{ color: "var(--stone)", lineHeight: 1.65 }}
            >
              Cara Capital is a venture fund of funds. In partnership with{" "}
              <span style={{ color: "var(--ink)" }}>Marram Ventures</span>, an investment firm combining capital, technology, and care innovation,
              we give oral health leaders access to investments that have historically been available only to large institutions.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="callout mt-10 max-w-3xl">
              <div className="label">Cara Strategics I</div>
              <div
                className="mt-4 text-[15.5px] md:text-[16px]"
                style={{ color: "var(--ink)", lineHeight: 1.6 }}
              >
                Our inaugural fund opens the door to healthcare investments, breakthrough innovations, KOL roles,
                and industry opportunities that move care convergence forward.
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.22}>
            <blockquote
              className="mt-10 md:mt-14 font-serif italic-serif max-w-3xl"
              style={{ fontSize: "clamp(22px, 2.6vw, 32px)", lineHeight: 1.35, color: "var(--ink)" }}
            >
              &ldquo;Cara Capital exists to expand access to healthcare innovation, investment opportunities, and wealth creation for women leaders in oral healthcare&mdash;building the gateway where{" "}
              <span className="grad-text">care convergence, capital, and leadership</span> intersect.&rdquo;
            </blockquote>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ value props ------------------------------- */
function ValueProps() {
  const items = [
    {
      n: "01",
      t: "Institutional-Quality Healthcare Investments",
      b: "Access to venture and private-market opportunities that have historically been reserved for the largest institutional allocators.",
    },
    {
      n: "02",
      t: "A Network of Influential Women in Dentistry",
      b: "Enter a curated community of clinicians, DSO leaders, PE professionals, and operators \u2014 already at the table together.",
    },
    {
      n: "03",
      t: "Industry Influence & Thought Leadership",
      b: "Speak at Cara programming, contribute to research, and help shape the care convergence conversation in oral health.",
    },
    {
      n: "04",
      t: "Career Advancement & Board Opportunities",
      b: "Board seats, advisory roles, executive placements, and pathways into healthcare companies that rarely surface publicly.",
    },
    {
      n: "05",
      t: "A Platform for Elevating Female Founders",
      b: "Meet and back women building the next generation of oral-health companies \u2014 through pitch days, mentorship, and follow-on capital.",
    },
  ];
  return (
    <section id="offers" className="section hairline-t">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">What Cara Offers</div>
          </Reveal>
        </div>
        <div className="md:col-span-9">
          <Reveal delay={0.05}>
            <h2
              className="font-serif max-w-4xl"
              style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 1.03, letterSpacing: "-0.02em" }}
            >
              More than capital.{" "}
              <span className="italic-serif grad-text">A wealth-and-influence platform.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-8 max-w-3xl text-[16px] md:text-[16.5px]"
              style={{ color: "var(--stone)", lineHeight: 1.65 }}
            >
              Membership in the Cara community means direct access to the opportunities, relationships, and information that historically only large institutions have enjoyed.
            </p>
          </Reveal>

          <div className="mt-12 border" style={{ borderColor: "var(--vein)" }}>
            {items.map((it, i) => (
              <Reveal key={it.n} delay={0.05 + i * 0.04}>
                <div
                  className={`grid grid-cols-12 gap-6 p-6 md:p-8 ${
                    i > 0 ? "border-t" : ""
                  }`}
                  style={{ borderColor: "var(--vein)" }}
                >
                  <div className="col-span-12 md:col-span-2">
                    <div
                      className="italic-serif"
                      style={{ fontSize: 34, color: "var(--ember)", lineHeight: 1 }}
                    >
                      {it.n}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <h3
                      className="font-serif"
                      style={{
                        fontSize: "clamp(20px, 2vw, 26px)",
                        lineHeight: 1.18,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {it.t}
                    </h3>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p
                      className="text-[14.5px] md:text-[15px]"
                      style={{ color: "var(--stone)", lineHeight: 1.6 }}
                    >
                      {it.b}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- manifesto ------------------------------- */
function Manifesto() {
  const chapters = [
    {
      n: "01",
      title: "A Hands-On Investing Experience",
      body: "Go beyond writing a check. Engage directly with founders, attend pitch sessions, and build a genuine understanding of venture, guided by operators inside oral health.",
    },
    {
      n: "02",
      title: "A National Community of Builders and Investors",
      body: "Join a growing community of investors, clinicians, DSO leaders, and PE professionals united by a shared commitment to advancing care convergence.",
    },
    {
      n: "03",
      title: "A Curated Portfolio Delivering Real Returns",
      body: "We back a select group of innovative oral health companies \u2014 the products, platforms, and technologies defining the next generation of care.",
    },
  ];
  return (
    <section id="why" className="section hairline-t">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">Why We Are Unique</div>
          </Reveal>
        </div>
        <div className="md:col-span-9">
          <Reveal delay={0.05}>
            <h2
              className="font-serif max-w-4xl"
              style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 1.03, letterSpacing: "-0.02em" }}
            >
              The only gateway fund in dentistry focused on{" "}
              <span className="italic-serif grad-text">care convergence.</span>
            </h2>
          </Reveal>

          <div className="mt-14 border" style={{ borderColor: "var(--vein)" }}>
            <div className="grid grid-cols-1 md:grid-cols-3">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={0.08 + i * 0.07}>
                  <div
                    className={`p-8 md:p-10 ${i > 0 ? "md:hairline-l" : ""} ${
                      i > 0 && "border-t md:border-t-0"
                    }`}
                    style={{ borderColor: "var(--vein)" }}
                  >
                    <div
                      className="italic-serif"
                      style={{ fontSize: 40, color: "var(--ember)", lineHeight: 1 }}
                    >
                      {c.n}
                    </div>
                    <h3
                      className="mt-8 font-serif"
                      style={{
                        fontSize: "clamp(22px, 2.2vw, 28px)",
                        lineHeight: 1.18,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {c.title}
                    </h3>
                    <p
                      className="mt-5 text-[14.5px]"
                      style={{ color: "var(--stone)", lineHeight: 1.6 }}
                    >
                      {c.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- marquee -------------------------------- */
function Marquee() {
  const item =
    "Bubbles, Big Ideas & Breakthroughs \u2726 Women in Dentistry Investor Social \u2726 Feb 24, 2027 \u2726 Chicago \u2726";
  const items = new Array(6).fill(item);
  return (
    <section className="hairline-t hairline-b overflow-hidden py-10 md:py-14">
      <div className="marquee-track">
        {[0, 1].map((k) => (
          <div key={k} className="flex items-center gap-16 shrink-0">
            {items.map((s, i) => (
              <span key={i} className="marquee-item">{s}</span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------- care convergence ---------------------------- */
function CareConvergence() {
  const items = [
    { t: "Systemic Connection", b: "Clinical consensus links oral health to the body\u2019s most costly conditions. Capital is just starting to follow." },
    { t: "A Market Redefined", b: "As DSO platforms scale, demand for integrated technology and patient-centered solutions is accelerating." },
    { t: "An Education Gap", b: "Most investors have never been given a clear path into oral health venture. Cara Capital opens that door." },
    { t: "First-Mover Positioning", b: "Oral health VC remains dramatically underfunded relative to its impact. Early movers hold asymmetric upside." },
  ];
  return (
    <section className="relative section overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ overflow: "hidden" }}>
        <video
          className="cc-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={CLOUDS_POSTER}
        >
          <source src={CLOUDS_WEBM} type="video/webm" />
          <source src={CLOUDS_MP4} type="video/mp4" />
        </video>
      </div>
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(248,245,240,0.92) 0%, rgba(248,245,240,0.78) 55%, var(--marble) 100%)",
        }}
      />
      <div className="container-editorial relative grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">Why Care Convergence</div>
          </Reveal>
        </div>
        <div className="md:col-span-9">
          <Reveal delay={0.05}>
            <h2
              className="font-serif max-w-4xl"
              style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 1.03, letterSpacing: "-0.02em" }}
            >
              Oral health is <span className="italic-serif grad-text">whole person health.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p
              className="mt-8 max-w-3xl text-[16.5px]"
              style={{ color: "var(--stone)", lineHeight: 1.65 }}
            >
              The science is settled. Oral health is clinically linked to cardiovascular disease, diabetes,
              Alzheimer&rsquo;s, and maternal health. The investment infrastructure is just getting started.
            </p>
          </Reveal>
          <div className="mt-14 border" style={{ borderColor: "var(--vein)" }}>
            <div className="grid grid-cols-1 md:grid-cols-2">
              {items.map((it, i) => (
                <Reveal key={it.t} delay={0.08 + i * 0.06}>
                  <div
                    className={`p-8 md:p-10 ${i % 2 === 1 ? "md:hairline-l" : ""} ${
                      i >= 2 ? "md:hairline-t" : ""
                    } ${i > 0 ? "border-t md:border-t-0" : ""}`}
                    style={{ borderColor: "var(--vein)", background: "rgba(255,255,255,0.55)" }}
                  >
                    <div className="microlabel">Pillar {String(i + 1).padStart(2, "0")}</div>
                    <h3
                      className="mt-4 font-serif"
                      style={{
                        fontSize: "clamp(22px, 2.4vw, 30px)",
                        lineHeight: 1.18,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {it.t}
                    </h3>
                    <p
                      className="mt-4 text-[14.5px]"
                      style={{ color: "var(--stone)", lineHeight: 1.6 }}
                    >
                      {it.b}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- leadership ------------------------------- */
const LEADERS = [
  { i: "CB", n: "Dr. Carolyn Brown", c: "DDS, MA", b: "Board member of a BlackRock Impact Opportunities Fund portfolio company. Entrepreneur and healthcare investor." },
  { i: "MF", n: "Mariya Filipova", c: "MBA", b: "Fortune 20 executive and healthcare investor with multiple successful exits across several funds." },
  { i: "SS", n: "Samantha Strain", c: "PE & Family Office Advisor", b: "DSO C-level leader, board member, and investor. Part of a team that executed a successful exit to KKR." },
  { i: "GH", n: "Dr. Gabriela Hricko", c: "DDS, MDS", b: "Chief Clinical Officer, Emerging Dental and Orthodontic Group. Harvard-trained orthodontist and angel investor." },
  { i: "SH", n: "Sonia Hounsell", c: "MBA", b: "CEO of Ascend Point. Former McKinsey brand strategist with a Shark Tank deal with Robert Herjavec." },
  { i: "VW", n: "Dr. Valerie Woo", c: "DMD", b: "Founder of NOVA Pediatric Dentistry, founding partner of Lightwave Dental. Harvard-trained pediatric dentist." },
  { i: "EV", n: "Dr. Emily Vanney", c: "DDS", b: "Chief Operating and Clinical Officer, United Dental Partners. Leader in scaling multi-site dental platforms." },
  { i: "HG", n: "Dr. Hazel Glasper", c: "DDS", b: "CEO of Re-Vive Dental. Whole person dentistry pioneer, featured on the cover of Dentistry Today." },
  { i: "KS", n: "Katrina M Sanders", c: "RDH, BSDH, M.Ed, RF", b: "Founder of Sanders Board Preparatory. Published author and sought-after speaker in dental education." },
];

function Leadership() {
  return (
    <section id="leadership" className="section hairline-t">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">Leadership &amp; Cara Council</div>
          </Reveal>
        </div>
        <div className="md:col-span-9">
          <Reveal delay={0.05}>
            <h2
              className="font-serif max-w-4xl"
              style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 1.03, letterSpacing: "-0.02em" }}
            >
              Driving care convergence.{" "}
              <span className="italic-serif grad-text">Opening access to opportunity.</span>
            </h2>
          </Reveal>

          <div className="mt-14 border" style={{ borderColor: "var(--vein)" }}>
            <div className="grid grid-cols-1 md:grid-cols-3">
              {LEADERS.map((l, i) => {
                const col = i % 3;
                const row = Math.floor(i / 3);
                return (
                  <Reveal key={l.n} delay={0.05 + (i % 3) * 0.05}>
                    <div
                      className="p-8 md:p-10"
                      style={{
                        borderLeft: col > 0 ? "1px solid var(--vein)" : "none",
                        borderTop: row > 0 ? "1px solid var(--vein)" : "none",
                      }}
                    >
                      <div className="monogram">{l.i}</div>
                      <h3
                        className="mt-6 font-serif"
                        style={{
                          fontSize: "clamp(20px, 1.8vw, 24px)",
                          lineHeight: 1.2,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {l.n}
                      </h3>
                      <div className="microlabel mt-2">{l.c}</div>
                      <p className="mt-4 text-[14px]" style={{ color: "var(--stone)", lineHeight: 1.6 }}>
                        {l.b}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ event teaser ------------------------------ */
function EventTeaser() {
  const navigate = useNavigate();
  const go = (e) => {
    e.preventDefault();
    navigate("/event");
  };
  const goRsvp = (e) => {
    e.preventDefault();
    navigate("/event#rsvp");
  };
  return (
    <section id="event" className="relative section hairline-t overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: `url(${ATHENA_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center 20%",
          filter: "grayscale(100%)",
          opacity: 0.14,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(248,245,240,0.72), rgba(248,245,240,0.86) 60%, var(--marble))",
        }}
      />
      <div className="container-editorial relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <Reveal>
            <div className="overline">Women in Dentistry Investor Social — Invite Only</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="mt-6 font-serif"
              style={{
                fontSize: "clamp(36px, 4.6vw, 64px)",
                lineHeight: 1.03,
                letterSpacing: "-0.02em",
              }}
            >
              Bubbles, Big Ideas <span className="italic-serif grad-text">& Breakthroughs.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              className="mt-4 italic-serif"
              style={{ fontSize: "clamp(18px, 1.8vw, 22px)", color: "var(--stone)" }}
            >
              Wednesday, Feb 24, 2027 &mdash; 5:30&ndash;7:30 PM &mdash; Chicago
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p
              className="mt-6 max-w-2xl text-[15.5px] md:text-[16px]"
              style={{ color: "var(--stone)", lineHeight: 1.65 }}
            >
              An evening of bubbly and great conversation with Cara Capital, the Cara Council, and Women in Dentistry at Level Two, Hyatt Centric Chicago Magnificent Mile. Perfectly timed after the Dental VIP Summit &mdash; no conference registration needed.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
          <Reveal delay={0.15}>
            <a href="/event" onClick={go} className="btn btn-ember">
              See the event →
            </a>
          </Reveal>
          <Reveal delay={0.2}>
            <a href="/event#rsvp" onClick={goRsvp} className="btn btn-outline">
              RSVP now
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- landing -------------------------------- */
function HashScroll() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // wait for content to be laid out
    const t = setTimeout(() => scrollToId(id), 250);
    return () => clearTimeout(t);
  }, [location.hash]);
  return null;
}

export default function Landing() {
  return (
    <div className="bg-marble" style={{ position: "relative" }}>
      <Grain />
      <Nav current="home" />
      <HashScroll />
      <Hero />
      <Stats />
      <About />
      <ValueProps />
      <Manifesto />
      <Marquee />
      <CareConvergence />
      <Leadership />
      <EventTeaser />
      <Footer />
    </div>
  );
}
